
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

type Category = 'all' | 'branding' | 'web-design';

interface PortfolioItem {
  id: number;
  title: string;
  client: string;
  category: 'branding' | 'web-design';
  image: string;
  description: string;
}

export default function Portfolio() {
  const { ref, isVisible } = useScrollAnimation<HTMLElement>();
  const [activeFilter, setActiveFilter] = useState<Category>('all');

  const portfolioItems: PortfolioItem[] = [
    {
      id: 1,
      title: 'Luxe Botanica Brand Identity',
      client: 'Luxe Botanica',
      category: 'branding',
      image: 'https://public.readdy.ai/ai/img_res/8bf156df8155a707152c3fa53068e61d.jpg',
      description: 'Complete brand identity for a premium skincare line'
    },
    {
      id: 2,
      title: 'TechFlow SaaS Platform',
      client: 'TechFlow Inc.',
      category: 'web-design',
      image: 'https://readdy.ai/api/search-image?query=modern%20SaaS%20dashboard%20website%20design%20mockup%20on%20laptop%20screen%20with%20clean%20teal%20and%20white%20interface%20data%20visualization%20charts%20minimalist%20UI%20professional%20workspace%20setting%20soft%20lighting&width=600&height=450&seq=port2&orientation=landscape',
      description: 'Modern dashboard and marketing website design'
    },
    {
      id: 3,
      title: 'Artisan Coffee Co. Rebrand',
      client: 'Artisan Coffee Co.',
      category: 'branding',
      image: 'https://readdy.ai/api/search-image?query=artisan%20coffee%20brand%20identity%20design%20mockup%20with%20kraft%20paper%20packaging%20coffee%20bags%20cups%20and%20business%20cards%20rustic%20wooden%20table%20warm%20earth%20tones%20professional%20flat%20lay%20photography%20clean%20composition&width=600&height=450&seq=port3&orientation=landscape',
      description: 'Full rebrand including packaging and visual identity'
    },
    {
      id: 4,
      title: 'Wellness Studio Website',
      client: 'Serenity Wellness',
      category: 'web-design',
      image: 'https://readdy.ai/api/search-image?query=wellness%20spa%20website%20design%20mockup%20on%20desktop%20screen%20with%20calming%20sage%20green%20and%20cream%20colors%20beautiful%20imagery%20clean%20typography%20booking%20interface%20modern%20minimalist%20layout%20professional%20presentation&width=600&height=450&seq=port4&orientation=landscape',
      description: 'Calming website with integrated booking system'
    },
    {
      id: 5,
      title: 'Urban Fitness Brand',
      client: 'Urban Fitness',
      category: 'branding',
      image: 'https://readdy.ai/api/search-image?query=bold%20fitness%20gym%20brand%20identity%20design%20mockup%20with%20black%20and%20orange%20colors%20athletic%20apparel%20water%20bottles%20towels%20and%20membership%20cards%20dynamic%20energetic%20style%20professional%20product%20photography%20dark%20background&width=600&height=450&seq=port5&orientation=landscape',
      description: 'Bold brand identity for a boutique fitness studio'
    },
    {
      id: 6,
      title: 'E-commerce Fashion Store',
      client: 'Moderno Fashion',
      category: 'web-design',
      image: 'https://readdy.ai/api/search-image?query=luxury%20fashion%20ecommerce%20website%20design%20mockup%20on%20imac%20screen%20with%20elegant%20black%20and%20white%20theme%20product%20grid%20layout%20sophisticated%20typography%20minimalist%20modern%20design%20professional%20studio%20setting&width=600&height=450&seq=port6&orientation=landscape',
      description: 'High-converting e-commerce platform design'
    }
  ];

  const filteredItems = activeFilter === 'all' 
    ? portfolioItems 
    : portfolioItems.filter(item => item.category === activeFilter);

  const filters: { label: string; value: Category }[] = [
    { label: 'All Work', value: 'all' },
    { label: 'Branding', value: 'branding' },
    { label: 'Web Design', value: 'web-design' }
  ];

  return (
    <section
      ref={ref}
      className={`py-24 bg-gradient-to-b from-slate-50 to-white transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className={`text-center mb-12 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          <span className="inline-block px-4 py-1.5 bg-[#00989e]/10 text-[#00989e] text-sm font-semibold rounded-full mb-4">
            Our Portfolio
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Featured Work
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Explore our latest branding and web design projects that have helped businesses stand out and grow
          </p>
        </div>

        {/* Filter Tabs */}
        <div className={`flex justify-center mb-12 transition-all duration-700 delay-100 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          <div className="inline-flex bg-gray-100 rounded-full p-1">
            {filters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setActiveFilter(filter.value)}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeFilter === filter.value
                    ? 'bg-[#00989e] text-white shadow-lg'
                    : 'text-gray-600 hover:text-slate-900'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {/* Portfolio Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredItems.map((item, index) => (
            <div
              key={item.id}
              className={`group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-2 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: isVisible ? `${(index + 2) * 80}ms` : '0ms' }}
            >
              <div className="relative overflow-hidden">
                <div className="w-full h-64">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                  <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                    <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-medium rounded-full mb-2">
                      {item.category === 'branding' ? 'Branding' : 'Web Design'}
                    </span>
                    <p className="text-white/90 text-sm">{item.description}</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <p className="text-[#00989e] text-sm font-medium mb-1">{item.client}</p>
                <h3 className="text-xl font-bold text-slate-900 group-hover:text-[#00989e] transition-colors">
                  {item.title}
                </h3>
              </div>
            </div>
          ))}
        </div>

        {/* View All CTA */}
        <div className={`text-center mt-14 transition-all duration-700 delay-300 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          <Link
            to="/case-studies"
            className="inline-flex items-center gap-2 px-8 py-4 bg-slate-900 text-white font-semibold rounded-full hover:bg-[#00989e] transition-all duration-300 hover:gap-3 cursor-pointer whitespace-nowrap"
          >
            View All Case Studies
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
